/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Component, inject, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HeaderComponent } from '../../shared/header.component';
import { CdkTableModule } from '@angular/cdk/table';
import { GroupsService } from '../groups.service';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { GroupDto, PostDto, SessionDto } from '@oracychampions/api-interfaces';
import { ConfirmComponent } from '../../shared/confirm.modal';
import { EditPostComponent } from './edit-post.component';
import { map, Observable } from 'rxjs';
import { PostComponent } from '../../shared/post.component';
import { NoItemsComponent } from '../../shared/no-items.component';
import { MarksheetComponent } from './marksheet.component';
import { EditSessionComponent } from '../sessions/edit-session.component';
import { ActionItemComponent } from '../../shared/action-item.component';
import { SessionPinComponent } from '../../session/session-pin.component';
import { LockService } from '../../core/lock.service';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { tablerChevronLeft, tablerChevronRight } from '@ng-icons/tabler-icons';
import { ShowImageComponent } from './show-image.component';

@Component({
  selector: 'oc-feed',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    CdkTableModule,
    PostComponent,
    NoItemsComponent,
    NgbDropdownModule,
    ActionItemComponent,
    NgIconComponent,
  ],
  providers: [
    provideIcons({
      tablerChevronLeft,
      tablerChevronRight,
    }),
  ],
  template: ` <div class="oc-feed">
    <div class="oc-feed-main">
      <h2 class="oc-feed-heading page-header">Recordings</h2>
      <ng-container *ngIf="posts$ | async as posts">
        <div *ngIf="posts.length > 0" class="oc-posts-list">
          <ng-container *ngFor="let post of posts$ | async">
            <oc-post *ngIf="!post.sessionId" [post]="post" icon="tablerUser" [actions]="actions(post)"></oc-post>
            <div *ngIf="post.sessionId && post.sessions.length" class="oc-feed-session">
              <h6 class="mb-3 page-header">{{ post.title }}</h6>

              <div class="oc-posts-sessions-container">
                <div class="oc-posts-sessions" (scroll)="onScroll($event)">
                  <div *ngFor="let sessionPost of post.sessions" class="oc-posts-sessions-post">
                    <oc-post
                      [post]="sessionPost"
                      icon="tablerUser"
                      [compact]="true"
                      [actions]="actions(sessionPost)"
                    ></oc-post>
                  </div>
                </div>
                <div class="oc-posts-sessions-shadow oc-posts-sessions-shadow-left"></div>
                <div class="oc-posts-sessions-shadow oc-posts-sessions-shadow-right"></div>
                <div class="oc-posts-sessions-move oc-posts-sessions-move-left" (click)="moveLeft($event)">
                  <ng-icon name="tablerChevronLeft" size="28" color="#ffffff"></ng-icon>
                </div>
                <div class="oc-posts-sessions-move oc-posts-sessions-move-right" (click)="moveRight($event)">
                  <ng-icon name="tablerChevronRight" size="28" color="#ffffff"></ng-icon>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
        <oc-no-items *ngIf="posts.length === 0"></oc-no-items>
      </ng-container>
    </div>
    <div class="oc-feed-side">
      <div ngbDropdown class="mb-4">
        <button ngbDropdownToggle class="btn btn-primary btn-rounded">New&nbsp;</button>
        <div ngbDropdownMenu>
          <button ngbDropdownItem (click)="addPost()">
            Post
            <div class="menu-subtitle">Record a single Post</div>
          </button>
          <button ngbDropdownItem (click)="addSession()">
            Pupil Time
            <div class="menu-subtitle">Create a session for a group of students</div>
          </button>
        </div>
      </div>
      <h3 class="mb-3 pt-1 page-header">Pupil Time</h3>
      <ul class="list-group">
        <a
          *ngFor="let session of sessions$ | async"
          class="oc-session-link list-group-item list-group-item-action d-flex justify-content-between align-items-center"
          (click)="launchSession(session)"
          routerLinkActive="active"
          ><div>{{ session.name }}</div>
          <div class="list-group-item-actions">
            <div ngbDropdown>
              <oc-action-item
                icon="tablerDots"
                ngbDropdownToggle
                [stopPropagation]="true"
                class="oc-session-menu"
              ></oc-action-item>
              <div ngbDropdownMenu>
                <button ngbDropdownItem (click)="launchSession(session, $event)">Launch</button>
                <div class="dropdown-divider"></div>
                <button ngbDropdownItem (click)="editSession(session, $event)">Edit</button>
                <button ngbDropdownItem (click)="deleteSession(session, $event)">Remove</button>
              </div>
            </div>
          </div></a
        >
      </ul>
    </div>
  </div>`,
  styles: `
    .oc-feed {
      display: flex;
      align-items: start;

      .oc-feed-main {
        flex: 1;
        padding: 1.5rem 1rem;
      }

      .oc-feed-side {
        position: sticky;
        top: 84px;
        min-width: 360px;
        padding-inline: 20px;
        margin-block: 24px;
      }

    }
    .oc-posts-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      max-width: 580px;
      margin-inline: auto;
    }

    oc-post {
      width: 100%;
    }

    .list-group-item .dropdown-toggle::after {
      display: none;
    }

    .list-group-item-actions {
      margin: -8px -16px -8px 0;
      display: flex;
      > * {
        margin-left: -4px;
      }
    }

    .oc-session-link {
      cursor: pointer;
    }

    .oc-posts-sessions-container {
      position: relative;
    }

    .oc-posts-sessions {
      &::-webkit-scrollbar {
        width: 0px;
        background: transparent; /* make scrollbar transparent */
      }
      gap: 10px;
      padding: 2px 0;
      display: flex;
      scroll-snap-type: x mandatory;
      scroll-padding: 40px;
      overflow-x: scroll;
      overflow-y: visible;

      > .oc-posts-sessions-post {
        min-width: 160px;
        width: 160px;
        height: 200px;
        scroll-snap-align: start;
      }
    }

    .oc-feed-session {
      margin: 1rem 0 0 0;
      

      &:first-child {
        margin: 0;
      }
    }

    .oc-session-menu {
      transform: scale(0.85);
      display: block;
    }

    .oc-feed-heading {
      max-width: 580px;
      margin-inline: auto;
      margin-bottom: 1.35rem;
    }

    .menu-subtitle {
      font-size: 0.8rem;
      color: #bbb;
      line-height: 1;
      margin-bottom: 6px;
    }

    .oc-posts-sessions-move {
      width: 40px;
      height: 40px;
      background: rgba(0,0,0,0.5);
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border-radius: 999px;
      display: none;

      ng-icon {
        pointer-events: none;
      }
    }

    .oc-posts-sessions-move-left {
      top: 50%;
      margin-top: -30px;
      left: -20px;

      ng-icon {
        position: relative;
        left: -1px;
      }
    }

    .oc-posts-sessions-move-right {
      top: 50%;
      margin-top: -30px;
      right: -20px;

      ng-icon {
        position: relative;
        left: 1px;
      }
    }

    .oc-posts-sessions-shadow {
      width: 20px;
      background: red;
      position: absolute;
      top: 0;
      bottom: 0;
      display: none;
    }

    .oc-posts-sessions-shadow-left {
      left: 0;
      background: linear-gradient(90deg, rgba(234,233,235,1) 0%, rgba(234,233,235,0) 100%);
    }

    .oc-posts-sessions-shadow-right {
      right: 0;
      background: linear-gradient(90deg, rgba(234,233,235,0) 0%, rgba(234,233,235,1) 100%);
    }
  `,
})
export class FeedComponent implements AfterViewInit {
  private groupsService = inject(GroupsService);
  private route = inject(ActivatedRoute);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private lockService = inject(LockService);
  private router = inject(Router);

  private groupId!: string;
  public group$: Observable<GroupDto>;

  public posts$: Observable<any[]>;
  public sessions$: Observable<SessionDto[]>;

  displayedColumns = ['title', 'createDate', 'actions'];

  constructor() {
    this.group$ = this.route.parent!.data.pipe(map((data) => data['group']));

    this.route.parent?.params.subscribe((params) => {
      this.groupId = params['id'];
      this.sessions$ = this.groupsService.getSessions(this.groupId);

      this.posts$ = this.groupsService.getPosts(this.groupId).pipe(
        map((posts) => {
          const sessionPostsGrouped = posts.reduce((res: any, val: PostDto) => {
            if (!val.sessionId) {
              return res;
            }
            (res[val.sessionId] = res[val.sessionId] || []).push(val);
            return res;
          }, {});

          const mostRecentPostFromEachSession = Object.values(sessionPostsGrouped).map(
            (posts: any) =>
              posts.sort(
                (a: PostDto, b: PostDto) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime(),
              )[0],
          );

          const noneSessionPosts = posts.filter((post) => !post.sessionId);

          const orderedPosts = noneSessionPosts
            .concat(mostRecentPostFromEachSession)
            .sort((a: PostDto, b: PostDto) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime());

          const postsWithAssociated = orderedPosts.map((post: any) => {
            if (post.sessionId) {
              post.sessions = sessionPostsGrouped[post.sessionId].sort(
                (a: PostDto, b: PostDto) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime(),
              );
            }

            return post;
          });

          return postsWithAssociated;
        }),
      );
    });
  }

  ngAfterViewInit() {
    // Massive hack - needs refacoring
    setTimeout(() => {
      document?.querySelectorAll('.oc-posts-sessions').forEach((element) => {
        this.drawScrollButtons(element as HTMLElement);
      });
    }, 200);
  }

  actions(post: PostDto): any[] {
    return post.imageId
      ? [
          {
            title: 'Marksheet...',
            action: () => {
              return this.marksheet(post);
            },
          },
          {
            title: 'Show Image...',
            action: () => {
              return this.showImage(post);
            },
          },
          {
            title: 'Edit',
            action: () => {
              return this.editPost(post);
            },
          },
          {
            title: 'Delete',
            action: () => {
              return this.deletePost(post);
            },
          },
        ]
      : [
          {
            title: 'Marksheet...',
            action: () => {
              return this.marksheet(post);
            },
          },
          {
            title: 'Edit',
            action: () => {
              return this.editPost(post);
            },
          },
          {
            title: 'Delete',
            action: () => {
              return this.deletePost(post);
            },
          },
        ];
  }

  addPost() {
    const modalRef = this.modalService.open(EditPostComponent, { fullscreen: true });
    modalRef.componentInstance.groupId = this.groupId;
    modalRef.closed.subscribe(() => {
      this.groupsService.reloadPosts();
    });
  }

  showImage(post: PostDto) {
    const modalRef = this.modalService.open(ShowImageComponent, { fullscreen: true });
    modalRef.componentInstance.post = post;
  }

  editPost(post: PostDto) {
    const modalRef = this.modalService.open(EditPostComponent, { fullscreen: true });
    modalRef.componentInstance.id = post.id;
    modalRef.componentInstance.groupId = this.groupId;
    modalRef.componentInstance.model = {
      title: post.title,
      students: post.students,
    };
    modalRef.closed.subscribe(() => {
      this.groupsService.reloadPosts();
    });
  }

  deletePost(post: PostDto) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.groupsService.deletePost(post.id).subscribe(() => {
          this.toastr.success('Post deleted');
          this.groupsService.reloadPosts();
        });
      }
    });
  }

  addSession() {
    const modalRef = this.modalService.open(EditSessionComponent, { fullscreen: true });
    modalRef.componentInstance.groupId = this.groupId;
    modalRef.closed.subscribe(() => {
      this.groupsService.reloadSessions();
    });
  }

  marksheet(post: PostDto) {
    const modalRef = this.modalService.open(MarksheetComponent, { fullscreen: true });
    modalRef.componentInstance.post = post;
  }

  editSession(session: SessionDto, event: Event) {
    event.stopPropagation();
    const modalRef = this.modalService.open(EditSessionComponent, { fullscreen: true });
    modalRef.componentInstance.id = session.id;
    modalRef.componentInstance.groupId = this.groupId;
    modalRef.componentInstance.model = {
      name: session.name,
      students: session.students,
    };
    modalRef.closed.subscribe(() => {
      this.groupsService.reloadSessions();
    });
  }

  deleteSession(session: SessionDto, event: Event) {
    event.stopPropagation();
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.groupsService.deleteSession(session.id).subscribe(() => {
          this.toastr.success('Pupil Time deleted');
          this.groupsService.reloadSessions();
        });
      }
    });
  }

  launchSession(session: SessionDto, event: Event | null = null) {
    event?.stopPropagation();
    // const modalRef = this.modalService.open(SessionPinComponent);
    // modalRef.closed.subscribe((pin) => {
    //   this.lockService.pin = pin;
    //   this.lockService.locked = true;
    //   this.router.navigate(['session', session.id]);
    // });

    this.router.navigate(['session', session.id]);
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;

    this.drawScrollButtons(element);
  }

  drawScrollButtons(element: HTMLElement) {
    const atStart = element.scrollLeft === 0;
    const atEnd = element.scrollLeft === element.scrollWidth - element.clientWidth;

    const leftButton = element.parentElement?.querySelector('.oc-posts-sessions-move-left');
    const rightButton = element.parentElement?.querySelector('.oc-posts-sessions-move-right');

    if (atStart) {
      leftButton?.classList.remove('d-flex');
    } else {
      leftButton?.classList.add('d-flex');
    }

    if (atEnd) {
      rightButton?.classList.remove('d-flex');
    } else {
      rightButton?.classList.add('d-flex');
    }
  }

  moveLeft(event: Event) {
    const element = (event.target as HTMLElement).parentElement?.querySelector('.oc-posts-sessions');
    const scrollX = element!.scrollLeft;

    if (scrollX > 0) {
      element?.scrollTo({ top: 0, left: scrollX - 170, behavior: 'smooth' });
    }
  }

  moveRight(event: Event) {
    const element = (event.target as HTMLElement).parentElement?.querySelector('.oc-posts-sessions');
    const scrollX = element!.scrollLeft;

    element?.scrollTo({ top: 0, left: scrollX + 170, behavior: 'smooth' });
  }
}
